import pythonApi from '../api/pythonApi';

class TemperatureService {
  private assetTypeUnitCache: Map<string, 'F' | 'C'> = new Map();
  
  // Expose cache for synchronous access in Utility
  getCache(): Map<string, 'F' | 'C'> {
    return this.assetTypeUnitCache;
  }

  /**
   * Convert temperature from one unit to another
   * @param value Temperature value
   * @param fromUnit Source unit ('F' or 'C')
   * @param toUnit Target unit ('F' or 'C')
   * @returns Converted temperature value
   */
  convertTemperature(value: number, fromUnit: 'F' | 'C', toUnit: 'F' | 'C'): number {
    if (fromUnit === toUnit) {
      return value;
    }

    if (fromUnit === 'F' && toUnit === 'C') {
      // Fahrenheit to Celsius: (F - 32) * 5/9
      return (value - 32) * 5 / 9;
    } else if (fromUnit === 'C' && toUnit === 'F') {
      // Celsius to Fahrenheit: (C * 9/5) + 32
      return (value * 9 / 5) + 32;
    }

    return value;
  }

  /**
   * Fetch all asset types and cache their temperature units
   * Call this once at app startup to avoid multiple API calls
   * @returns Promise that resolves when cache is populated
   */
  async initializeAssetTypeCache(): Promise<void> {
    try {
      const response = await pythonApi.get('/api/v1/asset-types');
      const assetTypes = response.data;

      if (assetTypes && typeof assetTypes === 'object') {
        // Populate cache with all asset types
        Object.keys(assetTypes).forEach((assetTypeId: string) => {
          const assetType = assetTypes[assetTypeId];
          const tempUnit = assetType?.temp_unit || 'F';
          const unit = (tempUnit === 'C' ? 'C' : 'F') as 'F' | 'C';
          this.assetTypeUnitCache.set(assetTypeId, unit);
        });
      }
    } catch (error) {
      console.error('Error initializing asset type temperature unit cache:', error);
    }
  }

  /**
   * Get temperature unit for an asset type (synchronous - uses cache)
   * @param assetType Asset type ID (e.g., 'rtu_bl1-1cd1-1cp1')
   * @returns 'F' or 'C' (defaults to 'F' if not found in cache)
   */
  getTemperatureUnit(assetType: string | null | undefined): 'F' | 'C' {
    // Default to Fahrenheit if no asset type
    if (!assetType) {
      return 'F';
    }

    // Check cache - if not found, default to F
    return this.assetTypeUnitCache.get(assetType) || 'F';
  }

  /**
   * Get temperature unit for an asset type (async - for backward compatibility)
   * @deprecated Use getTemperatureUnit() instead after cache is initialized
   * @param assetType Asset type ID (e.g., 'rtu_bl1-1cd1-1cp1')
   * @returns Promise resolving to 'F' or 'C'
   */
  async getTemperatureUnitAsync(assetType: string | null | undefined): Promise<'F' | 'C'> {
    // Check cache first
    if (assetType && this.assetTypeUnitCache.has(assetType)) {
      return this.assetTypeUnitCache.get(assetType)!;
    }

    // If not in cache, initialize cache (shouldn't happen if initialized properly)
    await this.initializeAssetTypeCache();
    
    return this.getTemperatureUnit(assetType);
  }

  /**
   * Format temperature value based on asset type
   * Database stores temperatures in Fahrenheit, so we convert if asset_type uses Celsius
   * @param value Temperature value in Fahrenheit (from database)
   * @param assetType Asset type ID
   * @param decimals Number of decimal places (default: 0 for display, 2 for graphs)
   * @returns Formatted temperature string with unit
   */
  async formatTemperature(value: number | null | undefined, assetType: string | null | undefined, decimals: number = 0): Promise<string> {
    if (value === null || value === undefined || isNaN(value)) {
      return '--';
    }

    const targetUnit = await this.getTemperatureUnit(assetType);
    let convertedValue = value;

    // Convert from F (database) to target unit
    if (targetUnit === 'C') {
      convertedValue = this.convertTemperature(value, 'F', 'C');
    }

    // Format with appropriate decimals
    const formattedValue = decimals === 0 
      ? Math.round(convertedValue)
      : convertedValue.toFixed(decimals);

    return `${formattedValue}°${targetUnit}`;
  }

  /**
   * Convert temperature value (synchronous version - requires unit to be known)
   * @param value Temperature value in Fahrenheit (from database)
   * @param targetUnit Target unit ('F' or 'C')
   * @param decimals Number of decimal places
   * @returns Converted and formatted temperature value
   */
  convertAndFormat(value: number | null | undefined, targetUnit: 'F' | 'C', decimals: number = 0): number {
    if (value === null || value === undefined || isNaN(value)) {
      return value as any;
    }

    if (targetUnit === 'C') {
      const converted = this.convertTemperature(value, 'F', 'C');
      return decimals === 0 ? Math.round(converted) : parseFloat(converted.toFixed(decimals));
    }

    return decimals === 0 ? Math.round(value) : parseFloat(value.toFixed(decimals));
  }

  /**
   * Get temperature unit symbol (synchronous - uses cache)
   * @param assetType Asset type ID
   * @returns Unit symbol ('°F' or '°C')
   */
  getTemperatureUnitSymbol(assetType: string | null | undefined): string {
    const unit = this.getTemperatureUnit(assetType);
    return `°${unit}`;
  }

  /**
   * Clear the cache (useful when asset types are updated)
   */
  clearCache(): void {
    this.assetTypeUnitCache.clear();
  }
}

export default new TemperatureService();